import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import { useMemo } from 'react';
import { useResearchDetailsCache } from './cache/useResearchDetailsCache';

export interface ResearchItemDetailsData {
  id: string;
  title: string;
  content: string;
  original_prompt: string;
  research_type: Json;
  sources: { title: string; url: string; snippet?: string; }[];
  related_questions: any[];
  research_parameters?: Record<string, any>;
  is_favorited?: boolean;
  favorite_count?: number;
  user_id?: string;
}

// Optimized domain extraction with memoization
const domainCache = new Map<string, string>();

const extractDomainName = (url: string): string => {
  if (!url) return 'Unknown Source';
  
  // Check cache first
  if (domainCache.has(url)) {
    return domainCache.get(url)!;
  }
  
  try {
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = `https://${url}`;
    }
    
    const urlObj = new URL(fullUrl);
    let domain = urlObj.hostname;
    
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }
    
    const result = domain.charAt(0).toUpperCase() + domain.slice(1);
    
    // Cache the result
    domainCache.set(url, result);
    return result;
  } catch (error) {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/]+)/);
    const result = match ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : 'Unknown Source';
    
    // Cache even fallback results
    domainCache.set(url, result);
    return result;
  }
};

// Optimized source processing with memoization
const processSource = (source: any) => {
  if (typeof source === 'string') {
    return {
      title: extractDomainName(source),
      url: source,
      snippet: ''
    };
  } else if (typeof source === 'object' && source !== null) {
    return {
      title: source.title || extractDomainName(source.url || ''),
      url: source.url || '',
      snippet: source.snippet || ''
    };
  } else {
    return {
      title: 'Unknown Source',
      url: '',
      snippet: ''
    };
  }
};

export const useResearchItemDetails = (id: string | null) => {
  const { getCachedDetails, setCachedDetails, invalidateCache } = useResearchDetailsCache();
  
  return useQuery({
    queryKey: ['research-item-details', id],
    queryFn: async (): Promise<ResearchItemDetailsData> => {
      if (!id) throw new Error('No research item ID provided');
      
      // ✅ CRITICAL FIX: Always invalidate in-memory cache when React Query refetches
      // This ensures we get fresh favorites data after toggles
      invalidateCache(id);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Batch queries for better performance
      const [researchResult, favoriteResult] = await Promise.all([
        // Main research query
        supabase
          .from('research_library')
          .select('id, title, content, original_prompt, research_type, sources, related_questions, research_parameters, favorite_count, user_id')
          .eq('id', id)
          .single(),
        
        // Favorite status query (only if user exists)
        user ? supabase
          .from('research_favorites')
          .select('id')
          .eq('research_id', id)
          .eq('user_id', user.id)
          .maybeSingle() : Promise.resolve({ data: null, error: null })
      ]);

      if (researchResult.error) throw researchResult.error;
      if (!researchResult.data) throw new Error('Research item not found');

      const data = researchResult.data;
      const is_favorited = !!favoriteResult.data;

      // Optimized source processing with memoization
      let sources = [];
      try {
        if (typeof data.sources === 'string') {
          sources = JSON.parse(data.sources);
        } else if (Array.isArray(data.sources)) {
          sources = data.sources;
        }
      } catch (e) {
        console.error('Error parsing sources:', e);
        sources = [];
      }

      // Process sources efficiently
      const processedSources = Array.isArray(sources) 
        ? sources.map(processSource)
        : [];

      // Handle research_parameters efficiently
      let research_parameters = {};
      try {
        if (typeof data.research_parameters === 'string') {
          research_parameters = JSON.parse(data.research_parameters);
        } else if (data.research_parameters && typeof data.research_parameters === 'object') {
          research_parameters = data.research_parameters;
        }
      } catch (e) {
        research_parameters = {};
      }

      // Handle related_questions efficiently
      let related_questions: any[] = [];
      try {
        if (Array.isArray(data.related_questions)) {
          related_questions = data.related_questions;
        } else if (data.related_questions) {
          const parsed = typeof data.related_questions === 'string' 
            ? JSON.parse(data.related_questions) 
            : data.related_questions;
          related_questions = Array.isArray(parsed) ? parsed : [];
        }
      } catch (e) {
        console.error('Error parsing related_questions:', e);
        related_questions = [];
      }

      const result: ResearchItemDetailsData = {
        id: data.id,
        title: data.title,
        content: data.content,
        original_prompt: data.original_prompt,
        research_type: data.research_type,
        sources: processedSources,
        related_questions,
        research_parameters,
        is_favorited,
        favorite_count: data.favorite_count || 0,
        user_id: data.user_id
      };

      // Cache the result for instant future access
      setCachedDetails(id, result);
      
      return result;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // ✅ Reduced from 10 to 5 minutes for fresher favorites data
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    retry: 1,
    // Enable background refetching for better UX
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
